<template>
  <div class="h-full flex flex-col bg-dark-primary">
    <!-- Header -->
    <div class="flex-shrink-0 px-4 py-3 border-b border-dark">
      <div>
        <h1 class="text-lg font-semibold text-dark-primary">Analysis</h1>
        <p class="text-sm text-dark-secondary">Detailed threat evidence and forensic data</p>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">

      <template v-if="!loading && evidenceData.length > 0">

        <!-- Section Header -->
        <div class="flex items-center gap-2">
          <Microscope class="w-4 h-4 segura-icon" />
          <h2 class="text-sm font-semibold text-dark-primary uppercase tracking-wide">Evidence</h2>
        </div>

        <!-- Evidence by Tactic -->
        <div class="space-y-1">
          <div v-for="tactic in evidenceData" :key="tactic.id"
               class="border border-dark rounded-lg overflow-hidden"
               :ref="el => setTechniqueRef(tactic.id, el)">

            <!-- Tactic Header -->
            <button
              @click="toggleTechnique(tactic.id)"
              class="w-full flex items-center justify-between px-3 py-2 bg-dark-tertiary hover:bg-dark-quaternary transition text-left"
            >
              <div class="flex items-center gap-2">
                <ChevronRight
                  class="w-3 h-3 text-dark-secondary transition-transform"
                  :class="{ 'rotate-90': expandedTechniques.has(tactic.id) }"
                />
                <span class="text-xs bg-error/10 text-error border border-error/30 rounded px-1.5 py-0.5 font-mono font-semibold">
                  {{ tactic.tactic_id }}
                </span>
                <span class="text-sm font-medium text-dark-primary">{{ tactic.id }}</span>
                <span class="px-1.5 py-0.5 text-xs bg-dark-quaternary text-dark-secondary rounded">
                  {{ tactic.detections.length }}
                </span>
              </div>
              <span class="text-xs font-semibold" :class="getRiskStyle(tactic.risk_level)">
                {{ tactic.risk_level }}
              </span>
            </button>

            <!-- Detections -->
            <div v-show="expandedTechniques.has(tactic.id)" class="bg-dark-primary">
              <div v-for="(ttp, idx) in tactic.detections" :key="idx"
                   class="border-t border-dark p-4 hover:bg-dark-tertiary/30 transition">

                <!-- Detection Header -->
                <div class="flex items-center gap-3 mb-3">
                  <div class="w-8 h-8 bg-dark-tertiary border border-dark rounded flex items-center justify-center flex-shrink-0">
                    <Camera class="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="text-xs font-mono text-error font-semibold">{{ ttp.technique_id }}</span>
                      <span v-if="ttp.sub_technique_id" class="text-xs font-mono text-dark-secondary">
                        → {{ ttp.sub_technique_id }}
                      </span>
                    </div>
                    <p class="text-sm font-medium text-dark-primary">{{ ttp.technique_name }}</p>
                    <div class="flex items-center gap-2 mt-1">
                      <span class="text-xs px-1.5 py-0.5 rounded" :class="getConfidenceStyle(ttp.confidence * 100)">
                        {{ Math.round(ttp.confidence * 100) }}% confidence
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Summary -->
                <p v-if="ttp.summary" class="text-xs text-dark-secondary leading-relaxed mb-3 ml-11">
                  {{ ttp.summary }}
                </p>

                <!-- Evidence Frames -->
                <div v-if="ttp.evidence_frame_ids?.length" class="ml-11">
                  <!-- Toggle link -->
                  <button
                    @click="toggleFrames(getTTPKey(tactic.id, ttp.technique_id))"
                    class="flex items-center gap-1.5 text-xs text-accent hover:text-accent/80 transition mb-2"
                  >
                    <Camera class="w-3 h-3" />
                    <span>{{ expandedFrames.has(getTTPKey(tactic.id, ttp.technique_id)) ? 'Hide frames' : 'Show frames' }}</span>
                    <span class="text-dark-tertiary">({{ ttp.evidence_frame_ids.length }})</span>
                  </button>

                  <!-- Frames grid -->
                  <div v-show="expandedFrames.has(getTTPKey(tactic.id, ttp.technique_id))"
                       class="space-y-3">
                    <div v-for="frameId in ttp.evidence_frame_ids" :key="frameId"
                         class="w-full h-32 bg-dark-secondary border border-dark rounded-lg overflow-hidden relative cursor-pointer"
                         @click="$emit('seek-frame', frameId)">
                      <img
                        v-if="loadedImages.has(frameId)"
                        :src="loadedImages.get(frameId)"
                        :alt="`Frame ${frameId}`"
                        class="w-full h-full object-cover"
                      />
                      <!-- Placeholder -->
                      <div v-else class="w-full h-full flex items-center justify-center">
                        <div class="text-center">
                          <Monitor class="w-8 h-8 text-dark-tertiary mx-auto mb-2" />
                          <p class="text-xs text-dark-tertiary font-mono">#{{ frameId }}</p>
                        </div>
                      </div>
                      <!-- Timestamp overlay -->
                      <div class="absolute bottom-2 right-2 bg-black/75 text-white px-2 py-1 rounded text-xs font-mono">
                        {{ formatFrameTime(frameId) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Procedure -->
                <div v-if="ttp.procedure" class="ml-11 mt-3 bg-dark-tertiary border border-dark rounded p-3">
                  <div class="flex items-center gap-2 mb-2">
                    <Terminal class="w-3 h-3 text-accent" />
                    <span class="text-xs text-dark-secondary uppercase tracking-wide">Procedure</span>
                  </div>
                  <p class="text-xs text-dark-primary leading-relaxed whitespace-pre-line">{{ ttp.procedure }}</p>
                </div>

              </div>
            </div>
          </div>
        </div>

      </template>

      <!-- Loading -->
      <template v-else-if="loading">
        <div class="flex flex-col items-center justify-center py-12">
          <div class="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-4"></div>
          <p class="text-sm text-dark-secondary">Loading forensic evidence...</p>
        </div>
      </template>

      <!-- Empty -->
      <template v-else>
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <Search class="w-12 h-12 text-dark-secondary opacity-40 mb-4" />
          <p class="text-sm text-dark-secondary mb-1">No evidence data available</p>
          <p class="text-xs text-dark-tertiary">Evidence analysis will appear here once processing is complete</p>
        </div>
      </template>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { ChevronRight, Camera, Monitor, Terminal, Search, Microscope } from "lucide-vue-next";
import { useSessionStore } from "../stores/sessionStore";
import { apiClient } from "../services/apiClient";
import { loadFrameImage, revokeFrameImages } from "../services/frameService";

const props = defineProps({
  session:          { type: Object, default: null },
  focusedTechnique: { type: String, default: null },
});

const emit = defineEmits(["seek-frame"]);

const sessionStore = useSessionStore();

// State
const loading          = ref(true);
const evidenceData     = ref([]);
const loadedImages     = ref(new Map()); // frameId → blob URL
const expandedTechniques = ref(new Set());
const expandedFrames   = ref(new Set()); // Track which TTP frames are visible
const techniqueRefs    = ref(new Map());

// Current session
const currentSession = computed(() => props.session || sessionStore.currentSession);

// ---------------------------------------------------------------------------
// Data loading — uses /detections (same as original AnalysisPanel)
// ---------------------------------------------------------------------------

async function loadEvidenceData() {
  const sessionId = currentSession.value?.id;
  if (!sessionId) return;

  loading.value = true;
  evidenceData.value = [];

  try {
    // Fetch detections — this is the correct endpoint, consistent with Overview
    const result = await apiClient(`/detections?session_id=${sessionId}`);
    const detections = result?.data ?? [];

    if (!detections.length) {
      console.log("[AnalysisPanel] No detections for", sessionId);
      return;
    }

    console.log(`[AnalysisPanel] ${detections.length} detections loaded`);

    // Group detections by tactic (same as original)
    const groups = {};
    detections.forEach(ttp => {
      const key = ttp.tactic_name || "Unknown";
      if (!groups[key]) groups[key] = { tactic_id: ttp.tactic_id, detections: [] };
      groups[key].detections.push(ttp);
    });

    // Build evidenceData
    evidenceData.value = Object.entries(groups).map(([tacticName, group]) => ({
      id:         tacticName,
      tactic_id:  group.tactic_id,
      risk_level: riskFromConfidence(group.detections),
      detections: group.detections,
    }));

    // Load frame images using evidence_frame_ids (guaranteed valid IDs)
    await loadAllImages(sessionId, detections);

  } catch (err) {
    console.error("[AnalysisPanel] loadEvidenceData failed:", err);
  } finally {
    loading.value = false;
  }
}

async function loadAllImages(sessionId, detections) {
  // Collect unique frame IDs from evidence_frame_ids (guaranteed valid by backend)
  const frameIds = new Set();
  detections.forEach(ttp => {
    (ttp.evidence_frame_ids ?? []).forEach(id => frameIds.add(id));
  });

  if (!frameIds.size) return;
  console.log(`[AnalysisPanel] Loading ${frameIds.size} evidence frames`);

  const entries = await Promise.all(
    [...frameIds].map(async (frameId) => {
      const url = await loadFrameImage(sessionId, frameId);
      return [frameId, url];
    })
  );

  loadedImages.value = new Map(entries.filter(([, url]) => url !== null));
  console.log(`[AnalysisPanel] ${loadedImages.value.size} images loaded`);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function riskFromConfidence(detections) {
  const max = Math.max(...detections.map(d => d.confidence ?? 0));
  if (max >= 0.8) return "HIGH";
  if (max >= 0.6) return "MEDIUM";
  return "LOW";
}

function formatFrameTime(frameId) {
  // frameId is in milliseconds per original AnalysisPanel
  const seconds = Math.floor(frameId / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function toggleTechnique(id) {
  const set = new Set(expandedTechniques.value);
  set.has(id) ? set.delete(id) : set.add(id);
  expandedTechniques.value = set;
}

function toggleFrames(key) {
  const set = new Set(expandedFrames.value);
  set.has(key) ? set.delete(key) : set.add(key);
  expandedFrames.value = set;
}

function getTTPKey(tacticId, techniqueId) {
  return `${tacticId}-${techniqueId}`;
}

function setTechniqueRef(id, el) {
  if (el) techniqueRefs.value.set(id, el);
}

function getRiskStyle(risk) {
  const map = { HIGH: "text-error", MEDIUM: "text-warning", LOW: "text-success" };
  return map[risk] ?? "text-dark-secondary";
}

function getConfidenceStyle(confidence) {
  if (confidence >= 80) return "bg-success/20 text-success";
  if (confidence >= 60) return "bg-warning/20 text-warning";
  return "bg-error/20 text-error";
}

async function scrollToTechnique(id) {
  await nextTick();
  const el = techniqueRefs.value.get(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

watch(() => currentSession.value?.id, async (newId) => {
  if (newId) {
    revokeFrameImages(loadedImages.value);
    loadedImages.value = new Map();
    await loadEvidenceData();
  }
});

watch(() => props.focusedTechnique, async (id) => {
  if (!id) return;
  expandedTechniques.value = new Set([...expandedTechniques.value, id]);
  await scrollToTechnique(id);
});

onMounted(async () => {
  console.log("[AnalysisPanel] Mounted, session:", currentSession.value?.id);
  await loadEvidenceData();

  if (props.focusedTechnique) {
    expandedTechniques.value = new Set([...expandedTechniques.value, props.focusedTechnique]);
    setTimeout(() => scrollToTechnique(props.focusedTechnique), 150);
  }
});

onBeforeUnmount(() => {
  revokeFrameImages(loadedImages.value);
});
</script>

<style scoped>
.segura-icon {
  color: #006b69;
}
html[data-theme="dark"] .segura-icon,
.dark .segura-icon {
  color: #00d1b5;
}
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 3px; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>